import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useLang, ui } from '../i18n/LanguageContext';
import { categories, services } from '../mock/data';

const Header = () => {
  const { lang, toggle, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServices, setMobileServices] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
    setMobileServices(false);
  }, [location]);

  const isHome = location.pathname === '/';
  const solid = scrolled || !isHome || mobileOpen;

  const navLinkClass = ({ isActive }) =>
    `link-underline text-sm tracking-wide transition-colors ${
      isActive ? 'text-champagne' : ''
    }`;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        solid ? 'bg-[#FBFAF8]/95 backdrop-blur-md border-b border-border py-3 shadow-sm' : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 flex items-center justify-between">
        <Link to="/" className="flex flex-col leading-none">
          <span className={`font-serif text-lg md:text-xl tracking-tight ${solid ? 'text-[#161514]' : 'text-white'}`}>
            Centre d’expertise
          </span>
          <span className={`eyebrow mt-1 ${solid ? 'text-champagne' : 'text-white/80'}`}>Familiale</span>
        </Link>

        {/* Desktop nav */}
        <nav className={`hidden lg:flex items-center gap-9 ${solid ? 'text-[#161514]' : 'text-white'}`}>
          <NavLink to="/" className={navLinkClass} end>{t(ui.nav.home)}</NavLink>
          <NavLink to="/a-propos" className={navLinkClass}>{t(ui.nav.about)}</NavLink>

          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <NavLink to="/services" className={`${navLinkClass({ isActive: location.pathname.startsWith('/services') })} flex items-center gap-1`}>
              {t(ui.nav.services)} <ChevronDown size={14} className={`transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
            </NavLink>
            <div
              className={`absolute left-1/2 -translate-x-1/2 top-full pt-4 transition-all duration-300 ${
                servicesOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
              }`}
            >
              <div className="w-[640px] bg-[#FBFAF8] border border-border shadow-xl p-7 grid grid-cols-2 gap-x-8 gap-y-6">
                {categories.map((cat) => (
                  <div key={cat.slug}>
                    <p className="eyebrow text-champagne mb-3">
                      {cat.path ? (
                        <Link to={cat.path} className="hover:text-[#161514] transition-colors">{t(cat.short)}</Link>
                      ) : (
                        t(cat.short)
                      )}
                    </p>
                    <ul className="space-y-2">
                      {cat.tabs
                        ? cat.tabs.map((tb) => (
                            <li key={tb.id}>
                              <Link to={`${cat.path}?tab=${tb.id}`} className="text-sm text-[#4a4740] hover:text-champagne transition-colors">
                                {t(tb.label)}
                              </Link>
                            </li>
                          ))
                        : services.filter((s) => s.category === cat.slug).map((s) => (
                            <li key={s.slug}>
                              <Link to={`/services/${s.slug}`} className="text-sm text-[#4a4740] hover:text-champagne transition-colors">
                                {t(s.title)}
                              </Link>
                            </li>
                          ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <NavLink to="/equipe" className={navLinkClass}>{t(ui.nav.team)}</NavLink>
          <NavLink to="/contact" className={navLinkClass}>{t(ui.nav.contact)}</NavLink>
        </nav>

        <div className={`hidden lg:flex items-center gap-5 ${solid ? 'text-[#161514]' : 'text-white'}`}>
          <button onClick={toggle} className="text-sm tracking-wide flex items-center gap-1 hover:text-champagne transition-colors">
            <span className={lang === 'fr' ? 'font-semibold' : 'opacity-60'}>FR</span>
            <span className="opacity-40">/</span>
            <span className={lang === 'en' ? 'font-semibold' : 'opacity-60'}>EN</span>
          </button>
          <Link
            to="/contact"
            className={`text-sm px-6 py-2.5 border transition-all duration-300 ${
              solid ? 'border-[#161514] hover:bg-[#161514] hover:text-white' : 'border-white hover:bg-white hover:text-[#161514]'
            }`}
          >
            {t(ui.cta.contact)}
          </Link>
        </div>

        {/* Mobile toggles */}
        <div className="flex lg:hidden items-center gap-4">
          <button onClick={toggle} className={`text-sm ${solid ? 'text-[#161514]' : 'text-white'}`}>
            {lang === 'fr' ? 'EN' : 'FR'}
          </button>
          <button onClick={() => setMobileOpen((o) => !o)} className={solid ? 'text-[#161514]' : 'text-white'} aria-label="Menu">
            {mobileOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-500 bg-[#FBFAF8] ${
          mobileOpen ? 'max-h-[85vh] overflow-y-auto border-t border-border' : 'max-h-0'
        }`}
      >
        <nav className="px-6 py-6 flex flex-col gap-1 text-[#161514]">
          <Link to="/" className="py-3 border-b border-border/60">{t(ui.nav.home)}</Link>
          <Link to="/a-propos" className="py-3 border-b border-border/60">{t(ui.nav.about)}</Link>

          <button
            onClick={() => setMobileServices((o) => !o)}
            className="py-3 border-b border-border/60 flex items-center justify-between"
          >
            {t(ui.nav.services)}
            <ChevronDown size={18} className={`transition-transform ${mobileServices ? 'rotate-180' : ''}`} />
          </button>
          {mobileServices && (
            <div className="pl-4 py-2 flex flex-col gap-3 border-b border-border/60">
              {categories.map((cat) => (
                <div key={cat.slug}>
                  <p className="eyebrow text-champagne mb-2">
                    {cat.path ? <Link to={cat.path}>{t(cat.short)}</Link> : t(cat.short)}
                  </p>
                  <div className="flex flex-col gap-2 pl-1">
                    {cat.tabs
                      ? cat.tabs.map((tb) => (
                          <Link key={tb.id} to={`${cat.path}?tab=${tb.id}`} className="text-sm text-[#4a4740]">
                            {t(tb.label)}
                          </Link>
                        ))
                      : services.filter((s) => s.category === cat.slug).map((s) => (
                          <Link key={s.slug} to={`/services/${s.slug}`} className="text-sm text-[#4a4740]">
                            {t(s.title)}
                          </Link>
                        ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <Link to="/equipe" className="py-3 border-b border-border/60">{t(ui.nav.team)}</Link>
          <Link to="/contact" className="py-3 border-b border-border/60">{t(ui.nav.contact)}</Link>
          <Link to="/contact" className="mt-4 text-center text-sm px-6 py-3 bg-[#161514] text-white">
            {t(ui.cta.contact)}
          </Link>
        </nav>
      </div>
    </header>
  );
};

export default Header;
